// Headless export registration for Brand Perception. Key `bpm` is the legacy pptx.coffee
// dispatch key; the sub-mode arrives in `params.mode` ('m' MDS | 't' Topography |
// 'q' Quadrant) and defaults to 'm', mirroring block.coffee modeTypes.
//
// `qa` is NOT registered here: it is a question of its own, drawn by the info-view quadrant
// chart, which owns its entry. `quadrant` is not registered anywhere — it is the editor-schema
// alias for `qa` and never reaches stats as a type.
// The report-param `bpm{key}` is a '!'-joined string ("m{mode}!p{plotBy}!e{elev}!
// b{brands.dotted}!a{attrs.dotted}!x..!y..!r..!c..") — the driver DECODES it and
// forwards the fields below; value defaults mirror the partials' defaultValues
// (all-positions for brand/attr lists, p:'e', e:'-1'/'-2', x:'1', y:'2', r/c:'none').
// Spec: .aidev/tasks/headless-charts-sdk-spike/docs/tasks/11-bpm.md

import {
  ExportableCharts,
  type ExportableChart
} from '@aytm/app/stats/page/question-views/components/charts/lib/exportable'

import MdsChart from '@aytm/app/stats/page/question-views/bpm/components/mds/MdsChart'
import { MODES } from '@aytm/app/stats/page/question-views/bpm/lib/modes'

const bpmEntry: ExportableChart = {
  // Each sub-mode is drawn by its own chart, which owns its readiness and its own svg clone --
  // so the sub-mode picks the component rather than a dispatcher switching inside one.
  Component: MdsChart,
  resolveComponent: (_data: any, params: any) => (MODES as Record<string, any>)[params.mode || 'm'].Chart,
  toProps: (data: any, params: any) => {
    const qvPositions = (data.questionVariants || []).map((v: any) => String(v.position))
    const batchPositions = (data.batches || []).map((b: any) => String(b.position))

    const tvPlotByValue = params.tvPlotByValue || data.defaultPlotByValue || 'e'
    // tv.coffee defaultValues: '-1' = all entities, '-2' = all attributes.
    const defaultValues = {
      p: data.defaultPlotByValue || 'e',
      e: { e: '-1', a: '-2' },
      b: qvPositions,
      a: batchPositions
    }

    return {
      id: String(data.id ?? data.questionKey),
      questionKey: data.questionKey,
      questionVariants: data.questionVariants,
      batches: data.batches,
      answer: data.answer,
      colorTheme: data.colorTheme,
      chartLegendMode: params.chartLegendMode,
      // Only 'map'/'none' matter (blank chart); any data mode renders normally.
      chartMode: params.chartMode || 'respondents',
      features: data.features || {},
      // mds
      mdsBrandsValue: params.mdsBrandsValue || qvPositions,
      mdsAttributesValue: params.mdsAttributesValue || batchPositions,
      // tv
      tvPlotByValue,
      tvElevationsRepresentValue:
        params.tvElevationsRepresentValue ?? (tvPlotByValue === 'a' ? defaultValues.e.a : defaultValues.e.e),
      tvBrandsValue: params.tvBrandsValue || qvPositions,
      tvAttributesValue: params.tvAttributesValue || batchPositions,
      // the topography chart reads its own defaults out of the bundle the block hands down
      defaultValues: { t: defaultValues },
      // quadrant
      scores: data.scores,
      crossing: data.crossing,
      plots: data.plots,
      title: data.title,
      dataLabels: params.dataLabels || data.dataLabels || 'hover',
      xAxisLimits: data.xAxisLimits,
      yAxisLimits: data.yAxisLimits,
      quadrantBrandsValue: params.quadrantBrandsValue || qvPositions,
      quadrantXAxisValue: params.quadrantXAxisValue || '1',
      quadrantYAxisValue: params.quadrantYAxisValue || '2',
      quadrantRadiusValue: params.quadrantRadiusValue || 'none',
      quadrantColorValue: params.quadrantColorValue || 'none'
    }
  }
}

ExportableCharts.register('bpm', bpmEntry)
